import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminSidebar } from '../../components/Sidebar';
import { useAuth } from '../../context/AuthContext';
import OnboardingWizard from '../../components/OnboardingWizard';
import { SkeletonStat, SkeletonCard } from '../../components/Skeleton';
import api from '../../utils/api';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

const fmt = (iso) => iso ? new Date(iso).toLocaleString('de-CH', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const dur = (start, end) => {
  if (!start || !end) return '';
  const m = Math.round((new Date(end) - new Date(start)) / 60000);
  return m < 60 ? `${m} min` : `${Math.floor(m/60)}h ${m%60}min`;
};

const PLAN_COLORS = { free: '#6b7280', standard: '#1A56DB', premium: '#8B6A1C' };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ color: p.color }}>
          {p.name}: <strong>{p.value}</strong>
        </div>
      ))}
    </div>
  );
};

export default function AdminDashboard() {
  const { mosque, plan } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(
    () => !localStorage.getItem('ilmify_onboarded')
  );

  useEffect(() => {
    api.get('/admin/dashboard')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const planName = plan?.plan || 'free';
  const planColor = PLAN_COLORS[planName] || '#6b7280';
  const daysLeft = plan?.expires_at
    ? Math.max(0, Math.ceil((new Date(plan.expires_at) - new Date()) / 86400000))
    : null;
  const planExpired = plan?.expires_at && new Date(plan.expires_at) < new Date();

  const sessionsUsed = data?.sessionsThisMonth ?? 0;
  const sessionsMax = data?.maxMonthlySessions ?? null;
  const sessionPct = sessionsMax ? Math.min(100, (sessionsUsed / sessionsMax) * 100) : 0;

  const chartData = data?.chartData ?? [];
  const hasChart = chartData.length > 0;

  return (
    <div style={{ display: 'flex' }}>
      {showOnboarding && <OnboardingWizard onClose={() => setShowOnboarding(false)} />}
      <AdminSidebar />
      <main className="main-content">

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 26, marginBottom: 4 }}>Willkommen, {mosque?.name}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 100,
              background: planColor + '18', color: planColor, textTransform: 'uppercase', letterSpacing: 0.5,
            }}>
              {planName}{plan?.is_trial ? ' Trial' : ''} Plan
            </span>
            {daysLeft !== null && !planExpired && (
              <span style={{ fontSize: 12, color: daysLeft <= 7 ? 'var(--error)' : 'var(--text3)' }}>
                Ablauf in {daysLeft} Tagen
              </span>
            )}
            {planExpired && (
              <span style={{ fontSize: 12, color: 'var(--error)', fontWeight: 600 }}>Plan abgelaufen</span>
            )}
          </div>
        </div>

        {/* Alerts */}
        {planExpired && (
          <div className="alert alert-warning" style={{ marginBottom: 20 }}>
            Ihr Plan ist abgelaufen. Kontaktieren Sie <strong>support@ilmify.ch</strong> für eine Verlängerung.
          </div>
        )}
        {mosque?.temp_password && (
          <div className="alert alert-warning" style={{ marginBottom: 20 }}>
            Bitte ändern Sie Ihr temporäres Passwort.{' '}
            <Link to="/admin/profile">Jetzt ändern</Link>
          </div>
        )}

        {/* Stats row */}
        <div className="grid-4" style={{ marginBottom: 28 }}>
          {loading ? (
            <>
              <SkeletonStat /><SkeletonStat /><SkeletonStat /><SkeletonStat />
            </>
          ) : (
            [
              { label: 'Quizze gesamt', value: data?.totalQuizzes ?? '—' },
              { label: 'Abgeschlossen', value: data?.finishedQuizzes ?? '—' },
              { label: 'Runden gespielt', value: data?.totalSessions ?? '—' },
              { label: 'Teilnehmer total', value: data?.totalParticipants ?? '—' },
            ].map(s => (
              <div key={s.label} className="stat-card">
                <div className="stat-value">{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))
          )}
        </div>

        <div className="grid-2" style={{ marginBottom: 28 }}>

          {/* Session limit */}
          {sessionsMax !== null && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Wettbewerbe diesen Monat
                  </div>
                  <div style={{ fontSize: 32, fontWeight: 800, marginTop: 6, color: sessionsUsed >= sessionsMax ? 'var(--error)' : 'var(--text)' }}>
                    {sessionsUsed} <span style={{ fontSize: 16, fontWeight: 400, color: 'var(--text3)' }}>/ {sessionsMax}</span>
                  </div>
                </div>
                {sessionsUsed >= sessionsMax && (
                  <a href="https://ilmify.ch/#pricing" target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 12, background: '#2563eb', color: '#fff', padding: '6px 14px', borderRadius: 8, textDecoration: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    Upgraden
                  </a>
                )}
              </div>
              <div style={{ height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', width: `${sessionPct}%`,
                  background: sessionsUsed >= sessionsMax ? 'var(--error)' : 'var(--primary)',
                  borderRadius: 4, transition: 'width 0.4s',
                }} />
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
                {planName === 'standard' ? 'Standard-Plan' : 'Free-Plan'}: {sessionsMax} Wettbewerbe/Monat
              </div>
            </div>
          )}

          {/* Quick actions */}
          <div className="card">
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 14 }}>
              Schnellstart
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Link to="/admin/quizzes/new" className="btn btn-primary" style={{ textAlign: 'center' }}>
                Neues Quiz erstellen
              </Link>
              <Link to="/admin/quizzes" className="btn btn-secondary" style={{ textAlign: 'center' }}>
                Alle Quizze anzeigen
              </Link>
              <Link to="/admin/questions" className="btn btn-secondary" style={{ textAlign: 'center' }}>
                Fragendatenbank verwalten
              </Link>
            </div>
          </div>
        </div>

        {/* Activity chart */}
        {loading ? (
          <SkeletonCard rows={4} />
        ) : hasChart ? (
          <div className="card" style={{ marginBottom: 28 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 20 }}>
              Aktivität der letzten 6 Monate
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'var(--text3)' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: 'var(--text3)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'var(--border)', opacity: 0.5 }} />
                <Bar dataKey="sessions" name="Runden" fill="var(--primary)" radius={[4,4,0,0]} maxBarSize={40} />
                <Bar dataKey="participants" name="Teilnehmer" fill="#A7C4B5" radius={[4,4,0,0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : null}

        {/* Recent sessions */}
        {loading ? (
          <SkeletonCard rows={5} />
        ) : data?.recentSessions?.length > 0 ? (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Letzte Spielrunden
              </div>
              <Link to="/admin/quizzes" style={{ fontSize: 12, color: 'var(--primary)' }}>Alle Quiz</Link>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {data.recentSessions.map((s, i) => (
                <div key={s.id} style={{
                  display: 'flex', alignItems: 'center', gap: 16, padding: '12px 0',
                  borderBottom: i < data.recentSessions.length - 1 ? '1px solid var(--border)' : 'none',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.title}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                      {fmt(s.started_at)} · {dur(s.started_at, s.finished_at)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center', minWidth: 52 }}>
                    <div style={{ fontWeight: 700, color: 'var(--primary)' }}>{s.participant_count}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>Teiln.</div>
                  </div>
                  <div style={{ textAlign: 'center', minWidth: 52 }}>
                    <div style={{ fontWeight: 700 }}>{s.top_score ?? '—'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>Top-Pkt.</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

      </main>
    </div>
  );
}
